import { styled } from 'linaria/lib/react';
import noiseTexture from 'assets/images/noise.png';
import { BREAKPOINTS, COLORS, FONTS } from 'styles/constants';

// Page Container - uses PageBackground component for gradient logic consistent with profile page
export const StyledPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  position: relative;
  width: 100%;
`;

// View Controls (back button)
export const StyledViewControls = styled.div`
  align-items: center;
  display: flex;
  height: 44px;
  justify-content: space-between;
  padding: 0 12px;
  position: relative;
  width: 100%;
  z-index: 2;
`;

export const StyledBackButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 22px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 10px;
  transition: background 0.15s ease;

  &:hover {
    background: ${COLORS.white[10]};
  }

  svg {
    fill: ${COLORS.white[100]};
    height: 24px;
    width: 24px;
  }
`;

// Header Section
export const StyledHeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 16px 0 0;
  position: relative;
  margin-top: 48px;
  width: 100%;
  z-index: 2;
`;

// Cover Card
export const StyledCoverCard = styled.div<{ bgImage?: string }>`
  aspect-ratio: 342 / 430;
  background-color: ${COLORS.brown.dark[100]};
  background-image: ${({ bgImage }) => (bgImage ? `url(${bgImage})` : 'none')};
  background-position: center;
  background-size: cover;
  border-radius: 10px;
  max-width: 342px;
  overflow: hidden;
  position: relative;
  width: calc(100% - 48px);
`;

export const StyledCoverCardVideo = styled.video`
  height: 100%;
  object-fit: cover;
  position: absolute;
  width: 100%;
`;

// Info Section
export const StyledInfoSection = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 24px;
  width: 100%;
`;

export const StyledButtonSection = styled.div<{ bgColor?: string; bgImage?: string }>`
  background-color: ${({ bgColor }) => bgColor ?? 'inherit'};
  background-image: ${({ bgImage }) => (bgImage ? `url(${bgImage})` : 'none')};
  background-repeat: repeat;
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 24px;
  width: 100%;
`;

// Owner User Section - shows when viewing own spark
export const StyledOwnerSection = styled.div<{ bgColor?: string; bgImage?: string }>`
  align-items: flex-start;
  align-self: center;
  background-color: ${({ bgColor }) => bgColor ?? 'inherit'};
  background-image: ${({ bgImage }) => (bgImage ? `url(${bgImage})` : 'none')};
  background-repeat: repeat;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0;
  width: 100%;
`;

export const StyledOwnerRow = styled.div`
  align-items: center;
  align-self: center;
  display: flex;
  gap: 8px;
  justify-content: center;
  padding: 0 24px;
  width: 390px;
`;

export const StyledOwnerInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 2px;
`;

export const StyledOwnerName = styled.span<{ isDark?: boolean }>`
  color: ${({ isDark }) => (isDark ? COLORS.white[100] : COLORS.brown.dark[100])};
  font: 600 14px/1.4 ${FONTS.secondary};
`;

export const StyledOwnerBio = styled.span<{ isDark?: boolean }>`
  color: ${({ isDark }) => (isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)')};
  font: 400 14px/1.4 ${FONTS.secondary};
`;

export const StyledOwnerThought = styled.p<{ isDark?: boolean }>`
  color: ${({ isDark }) => (isDark ? COLORS.white[100] : COLORS.brown.dark[100])};
  font: 600 14px/1.4 ${FONTS.secondary};
  margin: 0;
  padding: 0 24px;
  text-align: center;
  white-space: pre-wrap;
  width: 100%;
`;

// Empty state: "Tap to add your thoughts for this Spark" prompt
export const StyledThoughtEmptyState = styled.button<{ isDark?: boolean }>`
  background: transparent;
  border: none;
  color: ${({ isDark }) => (isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)')};
  cursor: pointer;
  font: 400 14px/1.4 ${FONTS.secondary};
  margin: 0;
  padding: 0 24px;
  text-align: center;
  width: 100%;

  &:active {
    opacity: 0.7;
  }
`;

// "How it works" button for empty state
export const StyledHowItWorksButton = styled.button`
  align-items: center;
  backdrop-filter: blur(2px);
  background: transparent;
  border: 1px solid ${COLORS.white[30]};
  border-radius: 22px;
  color: ${COLORS.white[100]};
  cursor: pointer;
  display: flex;
  font: 600 14px/1.4 ${FONTS.secondary};
  justify-content: center;
  padding: 12px 20px;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.7;
  }
`;

// Container for empty state content (centered below owner section)
export const StyledEmptyStateContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 48px 24px;
  width: 100%;
`;

export const StyledSparkDescription = styled.p`
  color: ${COLORS.white[100]};
  font: 600 14px/1.4 ${FONTS.secondary};
  max-width: 324px;
  text-align: center;
  white-space: pre-wrap;
`;

export const StyledCreateOwnButton = styled.button`
  align-items: center;
  background: ${COLORS.green.light};
  border: none;
  border-radius: 22px;
  color: ${COLORS.brown.dark[100]};
  cursor: pointer;
  display: flex;
  font: 600 14px/1.4 ${FONTS.secondary};
  justify-content: center;
  padding: 12px 20px;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.8;
  }
`;

// Body Section
export const StyledBodySection = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  width: 100%;
  z-index: 2;
`;

// Section Container
// The gradient stays solid until ~400px (roughly midpoint of first response card),
// then transitions to the base color by ~700px
// Uses a pseudo-element for the grain + gradient so it can be masked,
// while the base color (black/white) shows through when the mask fades out
export const StyledSection = styled.div<{ gradientEndColor?: string; isDark?: boolean }>`
  position: relative;
  background: ${({ isDark }) => (isDark ? COLORS.black[100] : COLORS.white[100])};
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 16px;
  width: 100%;
  align-self: center;

  &::before {
    content: ${({ gradientEndColor }) => (gradientEndColor ? '""' : 'none')};
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 0;
    background: ${({ gradientEndColor }) =>
      gradientEndColor
        ? `url(${noiseTexture}), linear-gradient(180deg, ${gradientEndColor} 0%, ${gradientEndColor} 350px, transparent 500px)`
        : 'none'};
    -webkit-mask-image: linear-gradient(180deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 1) 400px, rgba(0, 0, 0, 0) 700px);
    mask-image: linear-gradient(180deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 1) 400px, rgba(0, 0, 0, 0) 700px);
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-size: 100% 100%;
    mask-size: 100% 100%;
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

// Section Header
export const StyledSectionHeader = styled.div<{ isDark?: boolean }>`
  align-self: center;
  color: ${({ isDark }) => (isDark ? COLORS.white[100] : COLORS.brown.dark[100])};
  font: 400 22px/1.3 ${FONTS.primary};
  max-width: 538px;
  padding: 48px 24px 16px;
  width: 100%;
`;

export const StyledSectionIntro = styled.p<{ isDark?: boolean }>`
  color: ${({ isDark }) => (isDark ? COLORS.white[100] : COLORS.brown.dark[100])};
  font: 600 14px/1.4 ${FONTS.secondary};
  margin: 0;
  padding: 0 24px 0;
  text-align: center;
  white-space: pre-wrap;
`;

// Cards Container
export const StyledCardsContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 56px;
  padding: 12px 0 120px 0;
  width: 100%;
`;

// Spark Response Card
export const StyledSparkResponseCard = styled.div<{ isDark?: boolean }>`
  display: flex;
  flex-direction: column;
  max-width: 366px;
  width: calc(100% - 24px);
  color: ${({ isDark }) => (isDark ? COLORS.white[100] : COLORS.brown.dark[100])};
`;

// User Section (avatar + thought + reaction button)
export const StyledUserSection = styled.div<{ isOwner?: boolean }>`
  align-items: center;
  display: flex;
  gap: 8px;
  padding: ${({ isOwner }) => (isOwner ? '0 12px 12px 12px' : '0 12px 2px 12px')};
  width: 100%;
`;

export const StyledUserAvatar = styled.div<{ src?: string }>`
  background-color: ${COLORS.brown.light[100]};
  background-image: ${({ src }) => (src ? `url(${src})` : 'none')};
  background-position: center;
  background-size: cover;
  border-radius: 180px;
  flex-shrink: 0;
  height: 44px;
  overflow: hidden;
  width: 44px;
`;

export const StyledUserInfo = styled.div<{ isDark?: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  padding-top: 2px;
  color: ${({ isDark }) => (isDark ? COLORS.white[100] : COLORS.brown.dark[100])};
`;

export const StyledUserDetails = styled.div<{ isDark?: boolean }>`
  display: flex;
  flex-direction: column;
  font: 400 14px/1.4 ${FONTS.secondary};
  width: 100%;
`;

export const StyledUserNameRow = styled.div<{ isDark?: boolean }>`
  display: flex;
  width: 100%;

  & > span:first-child {
    color: ${({ isDark }) => (isDark ? COLORS.white[100] : COLORS.brown.dark[100])};
    font-weight: 600;
    flex-shrink: 0;
  }

  & > span:last-child {
    color: ${({ isDark }) => (isDark ? COLORS.white[100] : COLORS.brown.dark[100])};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const StyledUserBio = styled.span<{ isDark?: boolean }>`
  color: ${({ isDark }) => (isDark ? COLORS.white[100] : COLORS.brown.dark[100])};
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Thought Section
export const StyledThoughtSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

export const StyledThoughtRow = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  gap: 8px;
  min-width: 0;
  position: relative;
`;

export const StyledThoughtText = styled.div<{ isDark?: boolean }>`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: ${({ isDark }) => (isDark ? COLORS.white[100] : COLORS.brown.dark[100])};
  display: -webkit-box;
  flex: 1;
  font: 600 14px/1.4 ${FONTS.secondary};
  max-height: 40px;
  min-height: 1px;
  min-width: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const StyledReactionButton = styled.button`
  align-items: center;
  backdrop-filter: blur(2px);
  background: transparent;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  display: flex-start;
  flex-shrink: 0;
  height: 36px;
  justify-content: center;
  margin-left: auto;
  padding: 6px 8px;
  transition: background 0.15s ease;
  width: 36px;

  &:hover {
    background: ${COLORS.white[10]};
  }

  svg {
    height: 24px;
    width: 24px;
  }
`;

// Card Section
export const StyledCardSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

// Card body with gradient background (replicates Common Card style)
export const StyledCardBody = styled.div<{ bg: string }>`
  background: ${({ bg }) => bg};
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-height: 102px;
  overflow: hidden;
  padding: 20px 12px 12px;
  position: relative;
  width: 100%;
`;

// Media type tag (e.g., "Article", "Video")
export const StyledCardTag = styled.span`
  color: ${COLORS.white[100]};
  font: 12px/140% ${FONTS.secondary};
  position: relative;
  text-transform: capitalize;
`;

// Card title
export const StyledCardTitle = styled.span`
  color: ${COLORS.white[100]};
  display: block;
  font: 22px/130% ${FONTS.primary};
  padding-top: 4px;
  position: relative;
`;

// Inset image wrapper (replicates Common Card inset image)
export const StyledInsetImageWrap = styled.div`
  border-radius: 8px;
  box-shadow: 0 0 8px ${COLORS.brown.dark[10]};
  display: flex;
  overflow: hidden;
  position: relative;
  width: 100%;

  img {
    border-radius: 8px;
    height: auto;
    max-height: 506px;
    object-fit: cover;
    object-position: center;
    width: 100%;
  }
`;

// Legacy styled components (kept for compatibility)
export const StyledCardWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 430px;
  position: relative;
  width: 100%;
`;

export const StyledResponseCard = styled.div<{ bgImage?: string; bgGradient?: string }>`
  aspect-ratio: 342 / 430;
  background-color: ${COLORS.brown.dark[100]};
  background-image: ${({ bgImage, bgGradient }) => {
    if (bgImage) return `url(${bgImage})`;
    if (bgGradient) return bgGradient;
    return 'none';
  }};
  background-position: center;
  background-size: cover;
  border-radius: 8px;
  height: 430px;
  left: 50%;
  overflow: hidden;
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  width: 342px;
`;

// Footer with action buttons
export const StyledCardFooter = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  min-height: 20px;
  padding: 0 8px;
  width: 100%;
`;

export const StyledCardSource = styled.div`
  flex: 1;
`;

export const StyledActionButtons = styled.div`
  align-items: center;
  display: flex;
  gap: 2px;
`;

export const StyledActionButton = styled.button<{ isDark?: boolean }>`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 22px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 6px;
  transition: background 0.15s ease;

  &:hover {
    background: ${({ isDark }) => (isDark ? COLORS.brown.dark[10] : COLORS.white[10])};
  }

  svg {
    height: 24px;
    width: 24px;

    path {
      fill: ${({ isDark }) => (isDark ? COLORS.brown.dark[100] : COLORS.white[100])};
    }
  }
`;

// Loading State
export const StyledLoadingContainer = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: center;
  min-height: 200px;
`;

// Legacy styled exports for compatibility
export const StyledPageHeader = styled.div<{ bgImage?: string }>`
  background: ${({ bgImage }) => `url(${bgImage})`};
  background-size: cover;
  height: 547px;
  padding: 80px 24px 24px;
  position: relative;
  display: flex;
  justify-content: center;
`;

export const StyledInfoContainer = styled.div`
  width: 100%;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export const StyledSparkTitle = styled.div`
  font: 400 32px/120% ${FONTS.primary};
  color: ${COLORS.white[100]};
  width: 100%;
  max-width: 528px;
  margin: 0 auto;
  word-break: break-word;

  > span {
    width: 270px;
    display: block;
    @media screen and (min-width: ${BREAKPOINTS.s}px) {
      padding-left: 24px;
    }
  }
`;

export const StyledUserContainer = styled.div`
  margin: 0 -24px;
  padding: 20px 24px 0;
  border-top: 1px solid ${COLORS.white[30]};

  > div {
    width: 100%;
    max-width: 528px;
    margin: 0 auto;

    @media screen and (min-width: ${BREAKPOINTS.s}px) {
      padding-left: 24px;
    }
  }
`;

export const StyledResponseCounterContainer = styled.div`
  width: 100%;
  max-width: 528px;
  margin: 0 auto;
  padding: 32px 24px 0;
`;

export const StyledResponseCounter = styled.div<{ isDark: boolean }>`
  font: 400 22px/130% ${FONTS.primary};
  color: ${({ isDark }) => (isDark ? COLORS.white[100] : COLORS.brown.dark[100])};
`;

export const StyledNoResponsePlaceholder = styled.div<{ isDark: boolean }>`
  border-top: 1px solid ${COLORS.white.off[100]};
  padding: 28px 0 16px;
  font: 400 14px/140% ${FONTS.secondary};
  color: ${({ isDark }) => (isDark ? COLORS.white[100] : COLORS.brown.dark[100])};
`;

export const StyledResponseButton = styled.button`
  align-items: center;
  background-color: ${COLORS.brown.dark[100]};
  border-radius: 100px;
  bottom: 34px;
  box-shadow: 0px 11px 6px ${COLORS.black[10]}, 0px 5px 5px ${COLORS.black[20]}, 0px 1px 3px ${COLORS.black[20]},
    0px 0px 0px ${COLORS.black[20]};
  border: none;
  color: ${COLORS.white[100]};
  cursor: pointer;
  display: flex;
  font: 600 14px/140% ${FONTS.secondary};
  height: 58px;
  justify-content: center;
  left: 50%;
  margin: 0;
  outline: none;
  padding: 0;
  position: fixed;
  transform: translateX(-50%);
  width: 246px;
`;

export const StyledListWrap = styled.div`
  button {
    display: block;
    margin-left: auto;
    margin-right: auto;
    max-width: 342px;
    width: 100%;
  }
`;

// Floating Action Button for "Create your own"
export const StyledFloatingActionButton = styled.button<{ isVisible: boolean }>`
  align-items: center;
  backdrop-filter: blur(5px);
  background: ${COLORS.brown.dark[100]};
  border: none;
  border-radius: 25px;
  height: 48px;
  width: 174px;
  padding: 0 8px 0 0;
  bottom: ${({ isVisible }) => (isVisible ? '10px' : '84px')};
  box-shadow: 0px 11px 6px ${COLORS.black[10]}, 0px 5px 5px ${COLORS.black[20]}, 0px 1px 3px ${COLORS.black[20]};
  color: ${COLORS.white[100]};
  cursor: pointer;
  display: flex;
  font: 600 14px/1.4 ${FONTS.secondary};
  gap: 4px;
  justify-content: center;
  left: 50%;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  pointer-events: ${({ isVisible }) => (isVisible ? 'all' : 'none')};
  position: fixed;
  transform: translateX(-50%);
  transition: opacity 0.3s ease, bottom 0.3s ease;
  z-index: 100;

  &:active {
    transform: translateX(-50%) scale(0.98);
  }

  video {
    height: 32px;
    width: 32px;
  }
`;
